/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { merge, clone } from 'lodash';
import { createModal } from 'react-modal-promise';
import Pluralize from 'react-pluralize';
import { Typeahead } from 'react-bootstrap-typeahead';
import {
  Origin,
  ProtectionPlansSelectionType,
} from '@upstart/harmony/lib/constants';
import { ControlLabel, Form, FormControl, FormGroup } from 'react-bootstrap';
import DraggableList from 'components/DraggableList';
import { PriceAdjustmentType } from '@upstart/harmony/lib/legos/penTypes.js';
import '../ReactModal/reactModal.scss';

import { requiredValidationState } from '../../util/utils';
import LoadingOverlay from '../LoadingOverlay';
import MarkupDiscount from './MarkupDiscount';
import { Radio } from '../GlobalUI';
import { inStoreSelectionOptions, onlineSelectionOptions } from './conf';

function BundleModal(props) {
  const { actions, pen, dealer, close, bundle, condition, financeType } = props;
  const [productToAdd, setProductToAdd] = useState([]);
  const [productToExclude, setProductToExclude] = useState([]);
  const [name, setName] = useState(bundle.name);
  const [description, setDescription] = useState(bundle.description);
  // Old properties for markup/discount
  const [markup, setMarkup] = useState(bundle.markup);
  const [discount, setDiscount] = useState(bundle.discount);

  // New properties for markup/discount (based on deal origin)
  const [markups, setMarkups] = useState(bundle.markups || {});
  const [discounts, setDiscounts] = useState(bundle.discounts || {});
  const [image, setImage] = useState(bundle.image);
  const [enabled, setEnabled] = useState(bundle.enabled);

  const [selectedInStore, setSelectedInStore] = useState(
    bundle.selectedInStore || ProtectionPlansSelectionType.None,
  );
  const [selectedOnline, setSelectedOnline] = useState(
    bundle.selectedOnline || ProtectionPlansSelectionType.None,
  );

  const [displayHighlightedTextInput, setDisplayHighlightedTextInput] =
    useState(!!bundle?.highlightedText);

  const [highlightedText, setHighlightedText] = useState(
    bundle?.highlightedText || '',
  );

  const [submitted, setSubmitted] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const form = document.forms[0];
  let typeaheadInstance;

  const getProductList = (productIds) =>
    // sometimes the product does not exist
    productIds.map((id) => pen.penConfig.productMap[id]).filter((p) => !!p);
  const [products, setProducts] = useState(
    getProductList(bundle.productIds || []),
  );
  const [excludedProducts, setExcludedProducts] = useState(
    getProductList(bundle.excludedProductIds || []),
  );
  const [tempProductOrder, setProductOrder] = useState([...products]);
  const [excludedTempProductOrder, setExcludedProductOrder] = useState([
    ...excludedProducts,
  ]);
  const getAvailProducts = useCallback(() => {
    const payload = {
      dealerId: dealer.id,
      bundle,
      condition,
      financeType,
    };
    actions.getAvailProductsForBundleRequest(payload);
  }, [dealer, bundle, condition, financeType, actions]);

  useEffect(() => {
    getAvailProducts();
  }, [getAvailProducts]);

  useEffect(() => {
    setProductOrder([...products]);
  }, [products]);
  useEffect(() => {
    setExcludedProductOrder([...excludedProducts]);
  }, [excludedProducts]);

  const parseImage = (e) => {
    if (e) {
      const file = document.querySelector('#bundleImageUploadInput').files[0];
      setImage(file);
    }
  };

  const removeProduct = (row, listType) => {
    if (listType === 'products') {
      const index = bundle.productIds.findIndex((id) => id === row.id);
      if (index > -1) {
        bundle.productIds.splice(index, 1);
        setProducts(getProductList(bundle.productIds));
        if (bundle.productIds.length === 0) {
          setEnabled(false);
          setSelectedOnline(ProtectionPlansSelectionType.None);
          setSelectedInStore(ProtectionPlansSelectionType.None);
        }
        getAvailProducts();
      }
    }
    if (listType === 'excludedProducts') {
      const index = bundle.excludedProductIds.findIndex((id) => id === row.id);
      if (index > -1) {
        bundle.excludedProductIds.splice(index, 1);
        setExcludedProducts(getProductList(bundle.excludedProductIds));
        getAvailProducts();
      }
    }
  };

  const addSelectedProductToBundle = () => {
    if (!bundle.productIds) {
      bundle.productIds = [];
    }
    bundle.productIds.push(productToAdd.id);
    setProducts(getProductList(bundle.productIds));
    setProductToAdd([]);
    getAvailProducts();
  };

  const excludeSelectedProductFromBundle = () => {
    if (!bundle.excludedProductIds) {
      bundle.excludedProductIds = [];
    }
    bundle.excludedProductIds.push(productToExclude.id);
    setExcludedProducts(getProductList(bundle.excludedProductIds));
    setProductToExclude([]);
    getAvailProducts();
  };

  const validateForm = () => {
    setSubmitted(true);
    if (form.checkValidity() === false) {
      return false;
    }
    return true;
  };

  const saveBundle = () => {
    if (!validateForm()) {
      return;
    }
    setShowSpinner(true);
    const bundleCopy = clone(bundle);
    const dealerId = dealer.id;
    const bundleId = bundle.id;
    const formData = new FormData();
    if (tempProductOrder) {
      bundleCopy.productIds = tempProductOrder.map((p) => p.id);
    }
    if (excludedTempProductOrder) {
      bundleCopy.excludedProductIds = excludedTempProductOrder.map((p) => p.id);
    }

    formData.append(
      'bundle',
      JSON.stringify(
        merge(bundleCopy, {
          name,
          description,
          // Deprecated properties for markup/discount
          markup,
          discount,

          // New properties for markup/discount
          markups,
          discounts,
          enabled,
          selectedOnline,
          selectedInStore,
          highlightedText,
        }),
      ),
    );
    if (image) {
      formData.append('image', image);
    }
    if (bundleId) {
      actions.editBundleRequest({
        formData,
        dealerId,
        bundleId,
        condition,
        financeType,
      });
    } else {
      actions.createBundleRequest({
        formData,
        dealerId,
        condition,
        financeType,
      });
    }
    close(true);
  };

  const doesBundleHaveAllyProducts = useMemo(() => {
    const hasAlly = products.some(({ raw }) => raw?.providerId === 65);
    if (hasAlly) {
      setMarkup(0);
      setDiscount(0);
    }
    return hasAlly;
  }, [products]);

  const renderMarkupDiscountField = (label, origin, values, setValue, max) => (
    <div className="field col-sm-3">
      <MarkupDiscount
        label={`${label} - ${origin}`}
        submitted={submitted}
        value={values[origin]}
        readOnly={doesBundleHaveAllyProducts}
        setValue={(v) => setValue((prev) => ({ ...prev, [origin]: v }))}
        priceAdjustmentType={PriceAdjustmentType.PERCENT}
        max={max}
      />
    </div>
  );

  const onProductOrderChange = (newProductOrder) =>
    setProductOrder(newProductOrder);

  const onExcludedProductOrderChange = (newProductOrder) =>
    setExcludedProductOrder(newProductOrder);

  const actionsFormatter = React.useCallback(
    (product) => (
      <button
        className="btn btn-primary"
        onClick={(e) => {
          removeProduct(product, 'products');
          e.preventDefault();
        }}
      >
        Remove
      </button>
    ),
    [removeProduct],
  );

  const excludeActionsFormatter = React.useCallback(
    (product) => (
      <button
        className="btn btn-primary"
        onClick={(e) => {
          removeProduct(product, 'excludedProducts');
          e.preventDefault();
        }}
      >
        Remove
      </button>
    ),
    [removeProduct],
  );

  const commonColumns = [
    {
      displayName: 'Name',
      width: '40%',
      format(product) {
        return product?.raw?.name;
      },
    },
    {
      displayName: 'Display',
      width: '40%',
      format(product) {
        return product.displayName;
      },
    },
    {
      displayName: 'Enabled',
      width: '40%',
      format(product) {
        return product.enabled ? 'Enabled' : 'Disabled';
      },
    },
  ];
  const productColumns = [
    ...commonColumns,
    {
      displayName: 'Actions',
      width: '15%',
      format: actionsFormatter,
    },
  ];

  const excludeProductColumns = [
    ...commonColumns,
    {
      displayName: 'Actions',
      width: '15%',
      format: excludeActionsFormatter,
    },
  ];

  const handleInStoreSelection = (e) => {
    setSelectedInStore(e.target.value);
  };
  const handleOnlineSelection = (e) => {
    setSelectedOnline(e.target.value);
  };
  return (
    <div className="p-modal-container">
      <div className="p-modal large">
        <LoadingOverlay active={showSpinner}>
          <div className="p-modal-text">
            {bundle.id && (
              <h2>
                Edit {bundle.name} in {condition} - {financeType}
              </h2>
            )}
            {!bundle.id && (
              <h2>
                Add New Bundle for {condition} - {financeType}
              </h2>
            )}
          </div>
          <i
            onClick={() => close(false)}
            className="modal-close close icon"
            alt="close"
          />
          <div className="p-modal-body">
            <Form
              id="modifyBundleForm"
              name="modifyBundleForm"
              noValidate
              className="p-form"
            >
              <div className="row">
                <div className="field col-sm-12">
                  <FormGroup
                    controlId="name"
                    validationState={requiredValidationState(name, submitted)}
                  >
                    <ControlLabel>Name *</ControlLabel>
                    <FormControl
                      type="text"
                      className="form-control"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                    <FormControl.Feedback type="error">
                      <div className="p-form-field-message">
                        This is a Required Field
                      </div>
                    </FormControl.Feedback>
                  </FormGroup>
                </div>
                {renderMarkupDiscountField(
                  'Markup',
                  Origin.ONLINE,
                  markups,
                  setMarkups,
                )}
                {renderMarkupDiscountField(
                  'Markup',
                  Origin.INSTORE,
                  markups,
                  setMarkups,
                )}
                {renderMarkupDiscountField(
                  'Discount',
                  Origin.ONLINE,
                  discounts,
                  setDiscounts,
                  100,
                )}
                {renderMarkupDiscountField(
                  'Discount',
                  Origin.INSTORE,
                  discounts,
                  setDiscounts,
                  100,
                )}
              </div>
              {!!doesBundleHaveAllyProducts && (
                <div className=" col-sm-12 product-info-bundle">
                  * Bundles with Ally products cannot have discount/markup. Any
                  existing discount/markup will be removed.
                </div>
              )}
              <div className="row">
                <div className="field col-sm-12">
                  <label htmlFor="customerName">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="field col-sm-3">
                  <label htmlFor="userName">Enable Bundle</label>
                  <input
                    className="checkbox"
                    type="checkbox"
                    name="enable"
                    value={enabled}
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    disabled={
                      !bundle.productIds || bundle.productIds.length === 0
                    }
                  />
                </div>
                <div className="field col-sm-3">
                  <label htmlFor="product-is-taxed">
                    In Store Selection Setting
                  </label>
                  {inStoreSelectionOptions.map(({ label, value }) => (
                    <Radio
                      label={label}
                      value={value}
                      checked={value === selectedInStore}
                      onClick={handleInStoreSelection}
                      key={label + selectedInStore}
                    />
                  ))}
                </div>

                <div className="field col-sm-3">
                  <label htmlFor="product-is-taxed">
                    Online Selection Setting
                  </label>
                  {onlineSelectionOptions.map(({ label, value }) => (
                    <Radio
                      label={label}
                      value={value}
                      checked={value === selectedOnline}
                      onClick={handleOnlineSelection}
                      key={label + selectedOnline}
                    />
                  ))}
                </div>
                <div className="field col-sm-3 ">
                  <label htmlFor="highlight-bundle">Highlight Bundle</label>
                  <input
                    className="checkbox"
                    type="checkbox"
                    name="bundle-highlightedText-chechbox"
                    id="bundle-highlightedText-chechbox"
                    value={displayHighlightedTextInput}
                    checked={displayHighlightedTextInput}
                    onChange={(e) => {
                      setDisplayHighlightedTextInput(e.target.checked);
                      if (!e.target.checked) {
                        setHighlightedText('');
                      }
                    }}
                  />
                </div>

                {!!displayHighlightedTextInput && (
                  <div className="field col-sm-3">
                    <FormGroup
                      controlId="highlightedText"
                      validationState={requiredValidationState(
                        highlightedText,
                        submitted,
                      )}
                    >
                      <ControlLabel>Highlight Text *</ControlLabel>
                      <FormControl
                        type="text"
                        className="form-control"
                        name="highlightedText"
                        value={highlightedText}
                        onChange={(e) => setHighlightedText(e.target.value)}
                        required
                      />
                      <FormControl.Feedback type="error">
                        <div className="p-form-field-message">
                          This is a Required Field
                        </div>
                      </FormControl.Feedback>
                    </FormGroup>
                  </div>
                )}
              </div>
              <div className="row">
                {bundle.imageUrl && (
                  <div className="field col-sm-3">
                    <label>Bundle Image</label>
                    <div className="bundle-image-container">
                      <img src={bundle.imageUrl} alt="new upload" />
                    </div>
                  </div>
                )}
                <div className="field col-sm-6">
                  <div className="form-group">
                    <label htmlFor="bundleImageUploadInput">
                      Upload Bundle Image
                    </label>
                    <input
                      type="file"
                      className="form-control-file"
                      id="bundleImageUploadInput"
                      onChange={(e) => parseImage(e)}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="field col-sm-3 add-product-input-container">
                  <div className="product-typeahead-container">
                    <label htmlFor="product-typeahead">Add Product</label>
                    <Typeahead
                      id="product-typeahead"
                      ref={(typeahead) => {
                        typeaheadInstance = typeahead;
                      }}
                      placeholder="Add a product to this bundle"
                      className="product-typeahead"
                      selectHintOnEnter
                      clearButton
                      dropup
                      labelKey="displayName"
                      filterBy={['name']}
                      value={productToAdd}
                      options={pen.penConfig.availableProductsForBundle || []}
                      onChange={(product) => setProductToAdd(product[0])}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!productToAdd || productToAdd.length === 0}
                    onClick={() => {
                      addSelectedProductToBundle();
                      const instance = typeaheadInstance?.getInstance?.();
                      if (instance) {
                        instance.clear();
                      }
                    }}
                  >
                    Add &gt;
                  </button>
                </div>
                <div className="field col-sm-8">
                  <label>
                    <Pluralize
                      singular="Product"
                      plural="Products"
                      count={bundle.productIds.length}
                    />
                  </label>
                  <div className="bundle-product-container">
                    <DraggableList
                      columns={productColumns}
                      data={products}
                      onChange={onProductOrderChange}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="field col-sm-3 add-product-input-container">
                  <div className="product-typeahead-container">
                    <label htmlFor="exclude-product-typeahead">
                      Exclude Product
                    </label>
                    <Typeahead
                      id="exclude-product-typeahead"
                      ref={(typeahead) => {
                        typeaheadInstance = typeahead;
                      }}
                      placeholder="Exclude product from bundle"
                      className="product-typeahead"
                      selectHintOnEnter
                      clearButton
                      dropup
                      labelKey="displayName"
                      filterBy={['name']}
                      value={productToExclude}
                      options={pen.penConfig.availableProductsForBundle || []}
                      onChange={(product) => setProductToExclude(product[0])}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={
                      !productToExclude || productToExclude.length === 0
                    }
                    onClick={() => {
                      excludeSelectedProductFromBundle();
                      const instance = typeaheadInstance?.getInstance?.();
                      if (instance) {
                        instance.clear();
                      }
                    }}
                  >
                    Exclude &gt;
                  </button>
                </div>
                <div className="field col-sm-8">
                  <label>
                    <Pluralize
                      singular="Product"
                      plural="Products"
                      count={bundle?.excludedProductIds?.length || 0}
                    />
                  </label>
                  <div className="bundle-product-container">
                    <DraggableList
                      columns={excludeProductColumns}
                      data={excludedProducts}
                      onChange={onExcludedProductOrderChange}
                    />
                  </div>
                </div>
              </div>
            </Form>
          </div>
          <div className="p-modal-actions">
            <button
              className="btn btn-default btn-large"
              onClick={() => close(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-large btn-large-default"
              onClick={() => saveBundle()}
            >
              Save
            </button>
          </div>
        </LoadingOverlay>
      </div>
    </div>
  );
}

BundleModal.propTypes = {
  close: PropTypes.func,
  bundle: PropTypes.object,
  actions: PropTypes.object,
  dealer: PropTypes.object,
  pen: PropTypes.object,
  condition: PropTypes.string,
  financeType: PropTypes.string,
};

const mapStateToProps = ({ pen }) => ({
  pen,
});

const ModifyBundleModal = createModal(connect(mapStateToProps)(BundleModal));

export default ModifyBundleModal;
